import { Button, Divider, Modal, Text } from '@cotera/client/app/components/ui';
import { ChildrenProps, classNames } from '@cotera/client/app/components/utils';
import React, { useEffect, useRef } from 'react';
import { useKeyPress } from '@cotera/client/app/hooks/use-key-press';
import { makeStore, StateSetter } from '@cotera/client/app/etc';
import { cva, type VariantProps } from 'class-variance-authority';
import { AnimatePresence, motion } from 'motion/react';

type Child = ChildrenProps['children'];

const MODAL_MOTION_EASE = [0.4, 0, 0.2, 1] as const;

const hasFixedHeightSizing = (size: ModalSize): boolean => {
  if (size === 'lg') {
    return true;
  }

  if (size !== null && typeof size === 'object' && 'custom' in size) {
    return /(h-\[|h-screen|max-h-\[|max-h-screen)/.test(size.custom);
  }

  return false;
};

const modalSizeVariants = cva('flex flex-col', {
  variants: {
    size: {
      // `dvh`, not `vh`: on a mobile browser `100vh` is the viewport with the
      // URL bar retracted, so a full-height modal ran under the browser chrome
      // and took its footer — the row with the confirm button — with it.
      auto: 'min-w-[33vw] max-w-[90vw] max-h-[90dvh]',
      lg: 'w-screen h-dvh md:w-[66vw] md:h-[80vh] rounded-lg',
    },
  },
  defaultVariants: {
    size: 'auto',
  },
});

export type ModalSize =
  | VariantProps<typeof modalSizeVariants>['size']
  | { custom: string };

export type ModalItem = {
  view: () => Child;
  onClose?: () => void;
  size?: ModalSize;
  /**
   * When `top`, the overlay pins the panel below a fixed offset so height changes move the bottom edge only.
   * Only the **first** item in the stack controls this for the whole session so pushing views does not jump the shell.
   */
  verticalAlign?: 'center' | 'top';
};

type State = {
  history: ModalItem[];
  open: boolean;
};
const actions = (set: StateSetter<State>) => ({
  push: (child: ModalItem) => {
    set((s) => {
      return { history: [...s.history, child], open: true };
    });
  },
  replace: (child: ModalItem) => {
    set((_) => {
      return { history: [child], open: true };
    });
  },
  pop: () => {
    set((s) => {
      if (s.history.length >= 1) {
        const newHistory = s.history.slice(0, -1);
        return { history: newHistory, open: newHistory.length > 0 };
      }
      return s;
    });
  },
  close: () => {
    set(() => ({ history: [], open: false }));
  },
});

type Actions = ReturnType<typeof actions>;

const { hook: useProvider, provider: Provider } = makeStore<State, Actions>();

export const useModalManager = useProvider;

/**
 * The manager's actions mirrored onto a plain context. The store hook throws
 * when no provider is above it, which is fine for app views but not for shared
 * components (the data grid) that also render in tests and embeds; those ask
 * through here and fall back when the answer is null.
 */
const ModalActionsContext = React.createContext<Actions | null>(null);

/** The modal stack, or null when rendered outside a `ModalManagerProvider`. */
export const useOptionalModalManager = (): Actions | null =>
  React.useContext(ModalActionsContext);

/**
 * Whether the page rendering this is the one on top of the stack — i.e. the
 * page the user is actually looking at and interacting with. Every page in the
 * stack renders concurrently (see the `history.map` in `ModalManager`), so a
 * page cannot infer this from the stack depth alone; the manager, which knows
 * each page's index, hands it down here.
 *
 * Defaults to `true` so a `ModalPageView` rendered on its own (tests, one-off
 * embeds) behaves as before.
 */
const ModalPageIsTopContext = React.createContext<boolean>(true);

const ModalActionsBridge: React.FC<ChildrenProps> = ({ children }) => {
  const modalActions = useProvider((s) => s.actions);
  return (
    <ModalActionsContext.Provider value={modalActions}>
      {children}
    </ModalActionsContext.Provider>
  );
};

export const ModalManagerProvider: React.FC<ChildrenProps> = ({ children }) => {
  return (
    <Provider state={{ history: [], open: false }} actions={actions}>
      <ModalActionsBridge>{children}</ModalActionsBridge>
    </Provider>
  );
};

export const ModalManager: React.FC = () => {
  const close = useProvider((s) => s.actions.close);
  const pop = useProvider((s) => s.actions.pop);
  const open = useProvider((s) => s.open);
  const history = useProvider((s) => s.history);

  const lastItem = history.at(-1);
  const sizeConfig = lastItem?.size ?? 'auto';

  const sizeClassName =
    typeof sizeConfig === 'object' && 'custom' in sizeConfig
      ? sizeConfig.custom
      : modalSizeVariants({ size: sizeConfig as 'auto' | 'lg' });

  const isFixedHeightModal = hasFixedHeightSizing(sizeConfig);

  // Escape unwinds one page at a time. Closing the whole stack from a pushed
  // page used to throw away the list the user drilled in from, which made
  // Escape the wrong key to reach for anywhere but the root.
  useKeyPress('Escape', (event) => {
    if (!open) {
      return;
    }
    // A popover, select or dropdown inside the modal already consumed this.
    // Radix dismissable layers listen on `document` in the capture phase and
    // mark the event handled with `preventDefault()` without stopping it, so
    // this bubble-phase window listener would otherwise close the whole modal
    // on the same keypress that dismissed the layer.
    if (event.defaultPrevented) {
      return;
    }
    if (history.length > 1) {
      pop();
      history.at(-1)?.onClose?.();
      return;
    }
    close();
    history.forEach((item) => {
      item.onClose?.();
    });
  });

  if (!open) {
    return null;
  }

  const motionTransition = {
    layout: {
      duration: 0.28,
      ease: MODAL_MOTION_EASE,
    },
    opacity: { duration: 0.2, ease: MODAL_MOTION_EASE },
    scale: { duration: 0.8, ease: MODAL_MOTION_EASE },
  };

  const pageEnterExit = {
    opacity: 0,
    scale: 0.98,
  };

  const rootItem = history[0];
  const modalCenter = rootItem?.verticalAlign !== 'top';

  return (
    <Modal
      open={open}
      closeEnabled={false}
      center={modalCenter}
      // Sit above in-app chrome that carries its own stacking context — notably
      // the artifact-card resize handle (z-30) — so a modal is never painted
      // under the workflow editor's resize grip.
      priority="highest"
      onClose={() => {
        close();
        lastItem?.onClose?.();
      }}
      className="relative"
    >
      <motion.div
        layout="size"
        transition={motionTransition}
        className={classNames('flex flex-col overflow-hidden', sizeClassName)}
      >
        <AnimatePresence initial mode="popLayout">
          {history.map((item, index) => {
            const View = item.view;
            const isTop = index === history.length - 1;
            return (
              <motion.div
                key={index}
                transition={motionTransition}
                initial={isTop ? pageEnterExit : false}
                animate={
                  isTop ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 1 }
                }
                exit={pageEnterExit}
                className={classNames(
                  'w-full',
                  isTop
                    ? classNames(
                        'relative z-10 min-h-0',
                        // A continuous flex column (not `h-full` alone) so a
                        // scrollable body is height-bounded even when the modal
                        // sizes to `max-h-[..]` (an auto height that `h-full`
                        // can't resolve against). `h-full` stays for the
                        // definite-height modals that already rely on it.
                        isFixedHeightModal && 'flex flex-1 flex-col h-full'
                      )
                    : 'pointer-events-none absolute inset-0 h-full overflow-hidden'
                )}
                style={
                  isTop && !isFixedHeightModal
                    ? { height: 'fit-content' }
                    : undefined
                }
              >
                <motion.div
                  layout={isTop ? 'size' : false}
                  transition={motionTransition}
                  className={classNames(
                    'w-full min-h-0',
                    isTop && isFixedHeightModal && 'flex flex-1 flex-col h-full'
                  )}
                >
                  <ModalPageIsTopContext.Provider value={isTop}>
                    <View />
                  </ModalPageIsTopContext.Provider>
                </motion.div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </motion.div>
    </Modal>
  );
};

export const ModalPage: React.FC<{
  title?: string;
  children: ChildrenProps['children'];
  onClose?: () => void;
}> = ({ children, onClose = () => {}, title }) => {
  const manager = useModalManager((s) => s.actions);
  const isOpen = useModalManager((s) => s.open);

  useEffect(() => {
    if (isOpen) {
      manager.push({
        view: () => <ModalPageView title={title}>{children}</ModalPageView>,
        onClose,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children, isOpen]);

  return null;
};

const modalPageViewChildVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.2, ease: MODAL_MOTION_EASE },
  },
};

export const ModalPageView: React.FC<
  ChildrenProps & {
    title?: React.ReactNode;
    subtitle?: React.ReactNode;
    className?: string;
  }
> = ({ children, title, subtitle, className }) => {
  const history = useModalManager((s) => s.history);
  const back = useModalManager((s) => s.actions.pop);
  // The back arrow pops the top page, so it belongs only to the page currently
  // on top. Deriving it from `history.length` alone made every page beneath the
  // top one (e.g. the command palette with an action's modal pushed over it)
  // render an arrow too — briefly visible, and clipped, during the push/pop
  // crossfade while that page is still partly on screen.
  const isTopPage = React.useContext(ModalPageIsTopContext);
  const contentRef = useRef<HTMLDivElement>(null);

  // Land initial focus inside the modal ourselves. Target priority:
  //   1. an element the modal explicitly opts in with `data-autofocus` (escape
  //      hatch for the rare modal that wants a specific, non-first element);
  //   2. otherwise the first text field — so typing modals (folder name,
  //      rename) land in the input;
  //   3. otherwise the first button — so confirm modals land on their first
  //      (safe, non-destructive) button, e.g. Cancel.
  //
  // Why this reclaims over a window rather than focusing once: Headless UI's
  // Dialog runs its own focus pass that is *deferred* past our effect (and the
  // panel's contents animate in via Framer Motion), and it parks focus on the
  // panel container — so a single focus call gets overridden a frame or two
  // later and the user has to click in before a field/button responds. We keep
  // reclaiming focus whenever it lands *outside* the modal's content, which by
  // construction yields the moment focus is anywhere inside (a real field, or
  // wherever the user tabbed) and never fights a nested focus trap, since those
  // keep focus within this same subtree. The 400ms cap is a generous ceiling on
  // the Headless UI enter transition (~200ms) plus its deferred focus pass; once
  // focus is inside, every remaining tick is a cheap no-op, and the effect's
  // cleanup cancels the loop the instant the modal unmounts.
  useEffect(() => {
    const node = contentRef.current;
    if (node === null) {
      return;
    }

    const pickTarget = (): HTMLElement | null => {
      const explicit = node.querySelector<HTMLElement>('[data-autofocus]');
      if (explicit !== null) {
        return explicit;
      }
      const field = node.querySelector<HTMLElement>(
        'input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [contenteditable="true"]'
      );
      if (field !== null) {
        return field;
      }
      return node.querySelector<HTMLElement>(
        'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
      );
    };

    const deadline = performance.now() + 400;
    let frame = 0;
    const run = () => {
      const active = document.activeElement;
      // Only claim focus when it is not already somewhere inside the modal — so
      // we never yank the caret away from a field the user (or Headless UI)
      // already landed on.
      if (active === null || !node.contains(active)) {
        pickTarget()?.focus({ preventScroll: true });
      }
      if (performance.now() < deadline) {
        frame = requestAnimationFrame(run);
      }
    };
    frame = requestAnimationFrame(run);
    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <motion.div
      ref={contentRef}
      className={classNames(
        'flex flex-col w-full max-w-5xl min-h-0',
        className
      )}
      variants={{
        hidden: {},
        visible: {
          transition: { staggerChildren: 0.04, delayChildren: 0.02 },
        },
      }}
      initial="hidden"
      animate="visible"
    >
      {history.length > 1 && isTopPage && (
        <motion.div variants={modalPageViewChildVariants}>
          <Button
            inline
            compact
            small
            iconOnly
            icon="arrow-left"
            className="pt-0! pb-2! -mt-2 pl-0! w-fit"
            onClick={() => {
              back();
            }}
          />
        </motion.div>
      )}
      {title !== undefined && (
        <motion.div
          className={'flex flex-col'}
          variants={modalPageViewChildVariants}
        >
          <div className="flex items-start justify-between">
            <Text.Title
              className={subtitle !== undefined ? 'mb-1' : 'mb-2'}
              type="section"
            >
              {title}
            </Text.Title>
          </div>
          {subtitle !== undefined && (
            <span className="mb-4 text-sm text-muted-foreground">
              {subtitle}
            </span>
          )}
          <Divider className="mb-6" />
        </motion.div>
      )}
      <motion.div
        className="min-h-0 w-full flex-1 flex flex-col"
        variants={modalPageViewChildVariants}
      >
        {children}
      </motion.div>
    </motion.div>
  );
};
